import { Box, List, ListItemButton, ListItemText, Menu, Typography } from '@mui/material';
import { MouseEvent, useMemo, useState } from 'react';
import type { IcomxResourceType } from 'src/services/api/icomx/actions';
import { useIcomxChat } from './IcomxChatContext';
import IcomxLauncherButton from './IcomxLauncherButton';
import {
  filterLinkedIcomxCandidatesByTelecomSystem,
  getIcomxCandidateResourceDescriptor,
  type IcomxContactCandidate,
  type IcomxTelecomSystem,
} from './icomxContactCandidateModel';

interface IcomxInlineTelecomActionProps {
  resourceType?: IcomxResourceType | null;
  resourceId?: string | null;
  candidates?: IcomxContactCandidate[];
  system: IcomxTelecomSystem;
  tooltip: string;
  value: string | null | undefined;
  disabledReason?: string;
}

export default function IcomxInlineTelecomAction({
  resourceType,
  resourceId,
  candidates,
  system,
  tooltip,
  value,
  disabledReason,
}: IcomxInlineTelecomActionProps) {
  const { isEnabled, isLoading, isLaunching, openResourceConversation } = useIcomxChat();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const linkedCandidates = useMemo(
    () => filterLinkedIcomxCandidatesByTelecomSystem(candidates ?? [], system),
    [candidates, system]
  );

  if (!value) {
    return null;
  }

  const hasCandidateInput = !!candidates;
  const directResource =
    linkedCandidates.length === 1
      ? getIcomxCandidateResourceDescriptor(linkedCandidates[0], system)
      : null;
  const fallbackResource =
    resourceType && resourceId
      ? {
          resourceType,
          resourceId,
          selectedTelecom: { system, value },
        }
      : null;
  const launchResource = directResource ?? fallbackResource;
  const hasMultipleOptions = linkedCandidates.length > 1;

  if (hasCandidateInput && !linkedCandidates.length) {
    return null;
  }

  if (!hasMultipleOptions && (!launchResource?.resourceType || !launchResource?.resourceId)) {
    return null;
  }

  if (isLoading || !isEnabled) {
    return null;
  }

  if (!launchResource) {
    return null;
  }

  const handleOpenChooser = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseChooser = () => {
    setAnchorEl(null);
  };

  const handleSelectCandidate = (candidate: IcomxContactCandidate) => {
    const selectedResource = getIcomxCandidateResourceDescriptor(candidate, system);

    if (!selectedResource) {
      return;
    }

    handleCloseChooser();
    openResourceConversation(selectedResource);
  };

  if (hasMultipleOptions) {
    return (
      <>
        <Box component="span" data-testid={`icomx-inline-chooser-${system}`}>
          <IcomxLauncherButton
            appearance="inline"
            disabled={false}
            disabledReason={
              disabledReason ??
              `iComx requires linked FHIR resource data and an available ${system}.`
            }
            loading={isLaunching}
            onClick={handleOpenChooser}
            tooltip={tooltip}
          />
        </Box>
        <Menu
          anchorEl={anchorEl}
          data-testid={`icomx-inline-chooser-menu-${system}`}
          keepMounted
          onClose={handleCloseChooser}
          open={!!anchorEl}
          PaperProps={{
            sx: {
              width: 320,
              maxWidth: 'calc(100vw - 32px)',
              mt: 0.5,
            },
          }}
        >
          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Typography variant="subtitle2">Choose iComx Contact</Typography>
            <Typography color="text.secondary" variant="caption">
              Select the linked {system} contact to open in iComx.
            </Typography>
          </Box>
          <List disablePadding>
            {linkedCandidates.map((candidate) => (
              <ListItemButton
                data-testid={`icomx-inline-chooser-option-${candidate.id}`}
                key={candidate.id}
                onClick={() => handleSelectCandidate(candidate)}
              >
                <ListItemText
                  primary={candidate.label}
                  secondary={
                    <>
                      {candidate.sourceLabel ? (
                        <Typography color="text.secondary" component="span" variant="caption">
                          {candidate.sourceLabel}
                        </Typography>
                      ) : null}
                      <Typography component="span" display="block" variant="body2">
                        {system === 'phone' ? candidate.phone : candidate.email}
                      </Typography>
                    </>
                  }
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                />
              </ListItemButton>
            ))}
          </List>
        </Menu>
      </>
    );
  }

  if (hasCandidateInput && launchResource?.resourceType && launchResource?.resourceId) {
    return (
      <IcomxLauncherButton
        appearance="inline"
        disabled={false}
        disabledReason={
          disabledReason ?? `iComx requires linked FHIR resource data and an available ${system}.`
        }
        loading={isLaunching}
        onClick={() => openResourceConversation(launchResource)}
        tooltip={tooltip}
      />
    );
  }

  return (
    <IcomxLauncherButton
      appearance="inline"
      disabled={false}
      disabledReason={
        disabledReason ?? `iComx requires linked FHIR resource data and an available ${system}.`
      }
      loading={isLaunching}
      onClick={() => openResourceConversation(launchResource)}
      tooltip={tooltip}
    />
  );
}
